// Shared constants + helpers for landing-page sections (renderers + editors).
import {
  Frown, TrendingDown, HandCoins, Layers, Users, Moon,
  Target, LineChart, Wallet, Sparkles, Smartphone, Handshake,
  Rocket, CalendarCheck, Trophy, GraduationCap,
  School, Building2, Video, Brain,
  MousePointerClick, CreditCard, MessageCircle, CheckCircle2,
} from "lucide-react";

// Registry — string name → lucide icon component. Add new icons here as needed.
export const ICONS = {
  Frown, TrendingDown, HandCoins, Layers, Users, Moon,
  Target, LineChart, Wallet, Sparkles, Smartphone, Handshake,
  Rocket, CalendarCheck, Trophy, GraduationCap,
  School, Building2, Video, Brain,
  MousePointerClick, CreditCard, MessageCircle, CheckCircle2,
};

// Normalize section shape so unknown fields don't crash render.
export const arr = (v) => (Array.isArray(v) ? v : []);

export const SECTION_KINDS = [
  "hero", "features", "testimonials", "stats", "cta", "faq", "about", "pricing", "gallery",
  "registration_form",
  "waitlist", "countdown",
  "youtube_video", "instagram_embed", "video_embed", "button", "logos_strip",
  "contact_info", "steps", "image_text", "text_block",
  "custom_code",
];

export const SECTION_LABELS = {
  hero: "Hero", features: "Features", testimonials: "Testimonials", stats: "Stats",
  cta: "Call to action", faq: "FAQ", about: "About", pricing: "Pricing", gallery: "Gallery",
  registration_form: "Registration form",
  waitlist: "Waitlist", countdown: "Countdown",
  youtube_video: "YouTube video", instagram_embed: "Instagram", video_embed: "Video (MP4)",
  button: "Button", logos_strip: "Logos strip",
  contact_info: "Contact info", steps: "Steps / How it works",
  image_text: "Image + Text", text_block: "Text block",
  custom_code: "Custom HTML/CSS/JS",
};

// Default payloads used when a user adds a fresh section.
export function defaultSection(kind) {
  const base = { id: Math.random().toString(36).slice(2, 8), kind };
  switch (kind) {
    case "hero": return { ...base, title: "Your headline", subtitle: "A one-liner about what you do.", cta: "Get started", image: "" };
    case "features": return { ...base, title: "Features", items: [{ t: "Feature", d: "Describe the value." }] };
    case "testimonials": return { ...base, items: [{ q: "Great product.", n: "Customer", r: "Role" }] };
    case "stats": return { ...base, items: [{ n: "100+", l: "Happy customers" }] };
    case "cta": return { ...base, title: "Ready to get started?", subtitle: "Book a call today.", button: "Book now" };
    case "faq": return { ...base, items: [{ q: "Common question?", a: "Straightforward answer." }] };
    case "about": return { ...base, title: "About me", body: "A few lines about your story." };
    case "pricing": return { ...base, plans: [{ name: "Standard", price: "₹9,999", perks: ["Perk 1"] }] };
    case "gallery": return { ...base, images: [] };
    case "waitlist": return {
      ...base,
      title: "Join the waitlist",
      subtitle: "Be the first to know when the doors open.",
      button: "Notify me",
      success_message: "You're on the list. Check your email!",
      collect_name: true,
    };
    case "registration_form": return {
      ...base,
      title: "Register now",
      subtitle: "Bhar dijiye form aur hum aapse turant contact karenge.",
      button: "Submit",
      // Toggle each field
      fields: {
        name: true,
        mobile: true,
        email: false,
        city: true,
        requirement: false,
      },
      // Post-submit thank-you state
      success_title: "Thank you! Aapka form submit ho gaya.",
      success_message: "Hamari team jaldi aapse contact karegi. Neeche direct WhatsApp/Call bhi kar sakte hain.",
      whatsapp_number: "+91 98765 43210",
      call_number: "+91 98765 43210",
    };
    case "countdown": return {
      ...base,
      title: "Doors open in",
      target: new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString(),
    };
    case "youtube_video": return { ...base, title: "Watch this video", url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ" };
    case "instagram_embed": return { ...base, title: "Follow me on Instagram", handle: "@yourhandle", url: "https://instagram.com/yourhandle" };
    case "video_embed": return { ...base, title: "Watch this video", url: "", poster: "" };
    case "button": return { ...base, title: "", label: "Click here", url: "", style: "solid", new_tab: false };
    case "logos_strip": return { ...base, title: "As seen at", items: ["Company 1", "Company 2", "Company 3"] };
    case "contact_info": return { ...base, title: "Get in touch", items: [
      { kind: "whatsapp", value: "+91 98765 43210" },
      { kind: "email", value: "hello@example.com" },
      { kind: "phone", value: "+91 98765 43210" },
      { kind: "address", value: "Bengaluru, India" },
    ] };
    case "steps": return { ...base, title: "How it works", items: [
      { n: "1", t: "Step one", d: "Short description." },
      { n: "2", t: "Step two", d: "Short description." },
      { n: "3", t: "Step three", d: "Short description." },
    ] };
    case "image_text": return { ...base, title: "Side by side", body: "Body text next to an image.", image: "", cta: "", cta_url: "", reverse: false };
    case "text_block": return { ...base, title: "Section title", body: "Write a paragraph here." };
    case "custom_code": return {
      ...base,
      html: '<div style="text-align:center;padding:40px 20px;font-family:sans-serif">\n  <h2 style="font-size:32px;margin:0 0 12px">Custom section</h2>\n  <p style="color:#666;max-width:520px;margin:0 auto">Write your own HTML, CSS and JavaScript. It runs in a sandboxed iframe so it won\'t break the rest of your page.</p>\n</div>',
      css: '/* Add your styles here */\nbody { margin: 0; }',
      js: '// Add your JavaScript here (runs inside a sandboxed iframe)\nconsole.log("Custom code section loaded");',
      height: 260,
    };
    default: return base;
  }
}
